<template>
  <div v-if="showPopover" ref="popover" class="popover">
    <button class="clear-button absolute-top-right" data-test="close-button" @click="closePopover">
      <icon name="close-x" />
    </button>
    <div class="content-grid">
      <div v-if="iconName" class="col">
        <div class="icon-wrapper" :class="{ circle: useCircle }">
          <Icon :name="iconName" />
        </div>
      </div>
      <div class="col" :class="{ spanRow: !iconName }">
        <div class="popover-header">
          <slot name="header"></slot>
        </div>
        <span class="text-sm popover-text">
          <slot></slot>
        </span>
      </div>
    </div>
    <div class="caret" :class="position"></div>
  </div>
</template>

<script>
import Icon from '@/components/singletons/icon';

const CARET_SIZE = 8;

export default {
  components: {
    Icon,
  },
  props: {
    position: {
      type: String,
      default: 'top',
    },
    iconName: {
      type: String,
      default: '',
    },
    useCircle: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showPopover: true,
      updateCount: 0,
    };
  },
  mounted() {
    this.positionPopover();
    window.addEventListener('resize', this.positionPopover);
  },
  updated() {
    this.positionPopover();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.positionPopover);
  },
  methods: {
    closePopover() {
      this.showPopover = false;
    },
    positionPopover() {
      if (!this.$refs.popover) {
        return;
      }
      this.updateCount += 1;
      if (this.updateCount > 2) {
        return;
      }
      const popover = this.$refs.popover;
      const parent = popover.parentElement;
      const parentRect = parent.getBoundingClientRect();
      const popoverRect = popover.getBoundingClientRect();

      if (this.position === 'top') {
        // Center horizontally
        const leftPosition = (parentRect.width - popoverRect.width) / 2;
        popover.style.left = `${leftPosition}px`;

        // Position above the parent, accounting for the caret
        popover.style.top = `-${popoverRect.height + CARET_SIZE}px`;

        // Adjust if it goes off-screen
        const rightEdge = parentRect.left + leftPosition + popoverRect.width;
        if (rightEdge > window.innerWidth) {
          popover.style.left = 'auto';
          popover.style.right = '0';
        }
        if (parentRect.left + leftPosition < 0) {
          popover.style.left = '0';
        }

      } else if (this.position === 'bottom') {
        // Center horizontally
        const leftPosition = (parentRect.width - popoverRect.width) / 2;
        popover.style.left = `${leftPosition}px`;

        // Position below the parent, accounting for the caret
        popover.style.top = `${parentRect.height + CARET_SIZE + 8}px`;

        // Adjust if it goes off-screen
        const rightEdge = parentRect.left + leftPosition + popoverRect.width;
        if (rightEdge > window.innerWidth) {
          popover.style.left = 'auto';
          popover.style.right = '0';
        }
        if (parentRect.left + leftPosition < 0) {
          popover.style.left = '0';
        }
      } else if (this.position === 'right') {
        // Center vertically
        const topPosition = (parentRect.height - popoverRect.height) / 2;
        popover.style.top = `${topPosition}px`;

        // Position to the right of the parent, accounting for the caret
        popover.style.left = `${parentRect.width + CARET_SIZE + 4}px`;

        // Adjust if it goes off-screen
        const bottomEdge = parentRect.top + topPosition + popoverRect.height;
        if (bottomEdge > window.innerHeight) {
          popover.style.top = '0';
        }
        if (parentRect.top + topPosition < 0) {
          popover.style.top = '0';
        }
      } else if (this.position === 'left') {
        // Center vertically
        const topPosition = (parentRect.height - popoverRect.height) / 2;
        popover.style.top = `${topPosition}px`;

        // Position to the left of the parent, accounting for the caret
        popover.style.right = `${parentRect.width + CARET_SIZE}px`;

        // Adjust if it goes off-screen
        const bottomEdge = parentRect.top + topPosition + popoverRect.height;
        if (bottomEdge > window.innerHeight) {
          popover.style.top = '0';
        }
        if (parentRect.top + topPosition < 0) {
          popover.style.top = '0';
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
.content-grid {
  display: grid;
  grid-template-columns: 44px 1fr;
  gap: rem-calc(12);
  padding: rem-calc(6);
}

.col {
  display: flex;
  flex-direction: column;
  justify-content: center;

  &.spanRow {
    grid-column: span 2;
  }
}

.icon-wrapper {
  width: rem-calc(44);
  height: rem-calc(44);
  display: grid;
  place-items: center;
  background-color: #0C111D;
  color: #D0D5DD;
}

.circle {
  border-radius: 50%;
  border: 1.5px dashed #ffffff;
}

.popover {
  position: absolute;
  visibility: visible;
  opacity: 1;
  display: block;
  background-color: #0C111D;
  border: 1px solid #e3e8ef;
  border-radius: rem-calc(6);
  color: #D0D5DD;
  width: rem-calc(324);
  padding: rem-calc(8);
  z-index: 10;
}

.popover-header {
  font-weight: 500;
  margin-bottom: rem-calc(8);
}

.popover-text {
  text-wrap: wrap;
}

.caret {
  position: absolute;
  bottom: rem-calc(-8);
  width: 0;
  height: 0;
  border-left: rem-calc(8) solid transparent;
  border-right: rem-calc(8) solid transparent;
  border-top: rem-calc(8) solid #0C111D;

  &.right {
    left: -12px;
    top: calc(50% - 4px);
    transform: translateY(-50%);
    transform: rotate(90deg);
  }

  &.left {
    right: 100%;
    top: 50%;
    transform: translateY(-50%);
    transform: rotate(-90deg);
  }

  &.top {
    left: 50%;
    transform: translateX(-50%);
    top: 100%;
  }

  &.bottom {
    left: 50%;
    transform: translateX(-50%);
    transform: rotate(180deg);
    bottom: 100%;
  }
}

.clear-button {
  background: none;
  border: none;
  padding: 0;
  margin: 0;
}

.absolute-top-right {
  position: absolute;
  top: 0;
  right: 0;
  cursor: pointer;
  z-index: 20;
}
</style>
